// ABOUTME: Renders one custom page layout column: its widgets ordered by position, then its tabs.
// ABOUTME: Holds the per-widget cell logic (width, margin, padding, sticky, Suspense fallback).

import { Box, useTheme } from '@ds/coolshop';
import { useAuth } from '@src/hooks';
import { ReactNode, Suspense } from 'react';

import { CustomPageLayout, CustomPageWidget } from '../types';
import { visibleTabs } from '../utils/visibleTabs';
import { visibleWidgets } from '../utils/visibleWidgets';
import { widgetCellWidth } from '../utils/widgetCellWidth';
import { NodeInfoLoadingSkeleton } from '../widgets/components/NodeInfoLoadingSkeleton';
import { WidgetFrame } from '../widgets/components/WidgetFrame';
import { WIDGET_TYPE_SKELETON_VARIANT, WidgetSkeleton } from '../widgets/components/WidgetSkeleton';
import widgetRegistry from '../widgets/registry';
import { CARD_VIEW_DEFAULTS_BY_WIDGET, parseCardViewConfig, WidgetProps } from '../widgets/types';
import { cardSkeletonProps } from '../widgets/utils/cardLayout';
import { ColumnTabs } from './ColumnTabs';
import { PAGE_CONTENT_PADDING } from './pageContentPadding';

export interface LayoutColumnProps {
    column: CustomPageLayout['columns'][number];
    context: Omit<WidgetProps, 'config'>;
    gap?: number;
    /** The column is a side panel, which pads its content itself. */
    sidePanel?: boolean;
    /** The column is the main (center) column of the page, not one nested in a tab. */
    pageColumn?: boolean;
}

// The widgets that lay a whole page out and fill its height when told to (`fillHeight`).
const FILL_HEIGHT_WIDGET_TYPES: ReadonlySet<string> = new Set(['model-selection']);

// A data table that is the only widget of a list page fills the column height and scrolls inside
// it, so the page itself does not scroll. A configured `maxHeight` keeps it at that height.
function isColumnFillingTable(widget: CustomPageWidget): boolean {
    return (
        widget.type === 'node-list' &&
        widget.config?.viewMode !== 'card' &&
        widget.config?.maxHeight == null
    );
}

function parseWidgetWidth(config?: Record<string, unknown>): number {
    const raw = config?.width;
    if (typeof raw === 'number' && raw > 0 && raw <= 1) return raw;
    return 1;
}

interface WidgetMargin {
    mx?: number;
    my?: number;
    mt?: number;
    mb?: number;
    ml?: number;
    mr?: number;
}

function parseWidgetMargin(config?: Record<string, unknown>): WidgetMargin | undefined {
    const raw = config?.margin;
    if (!raw || typeof raw !== 'object') return undefined;
    const margin = raw as Record<string, unknown>;
    const result: WidgetMargin = {};
    if (typeof margin.x === 'number') result.mx = margin.x;
    if (typeof margin.y === 'number') result.my = margin.y;
    if (typeof margin.t === 'number') result.mt = margin.t;
    if (typeof margin.b === 'number') result.mb = margin.b;
    if (typeof margin.l === 'number') result.ml = margin.l;
    if (typeof margin.r === 'number') result.mr = margin.r;
    return Object.keys(result).length > 0 ? result : undefined;
}

interface WidgetPadding {
    px?: number;
    py?: number;
    pt?: number;
    pb?: number;
    pl?: number;
    pr?: number;
}

// Breathing room around every widget of the grid, in MUI spacing units: 8px horizontally
// and 6px vertically. A widget's own `padding` config overrides it per side.
const DEFAULT_CELL_PADDING: WidgetPadding = { px: 1, py: 0.75 };

// Space between the widgets of a column whose cells have no default padding, in MUI spacing units.
const PADDED_COLUMN_GAP = 2;

// `defaults` is the column's cell padding, used as is by a widget without a `padding` config; a
// widget's own `padding` overrides the framework default per side, so the sides it leaves out keep
// that default whatever column the widget sits in.
function parseWidgetPadding(
    config: Record<string, unknown> | undefined,
    defaults: WidgetPadding,
): WidgetPadding {
    const raw = config?.padding;
    if (!raw || typeof raw !== 'object') return defaults;
    const padding = raw as Record<string, unknown>;
    const result: WidgetPadding = { ...DEFAULT_CELL_PADDING };
    if (typeof padding.x === 'number') result.px = padding.x;
    if (typeof padding.y === 'number') result.py = padding.y;
    if (typeof padding.t === 'number') result.pt = padding.t;
    if (typeof padding.b === 'number') result.pb = padding.b;
    if (typeof padding.l === 'number') result.pl = padding.l;
    if (typeof padding.r === 'number') result.pr = padding.r;
    return result;
}

// The row geometry a widget cell is sized against: the gap between the cells of a row and the
// theme spacing that turns the margin units into CSS lengths.
interface CellRow {
    gap: string;
    spacing: (units: number) => string;
}

function renderWidget(
    widget: CustomPageWidget,
    context: Omit<WidgetProps, 'config'>,
    widgetIndex: number,
    stickyZIndex: number,
    row: CellRow,
    cellPadding: WidgetPadding,
    // Set when the column is padded as page content: the width a top action bar takes to win that
    // padding back and stay a bar running edge to edge at the top of the content.
    bleedWidth?: string,
) {
    const WidgetComponent = widgetRegistry[widget.type];
    if (!WidgetComponent) return null;

    const widgetConfig = widget.config ? (widget.config as Record<string, unknown>) : undefined;
    const margin = parseWidgetMargin(widgetConfig);
    // `width` is the share of the row the widget occupies, its gap share and margins included.
    const marginX = row.spacing((margin?.ml ?? margin?.mx ?? 0) + (margin?.mr ?? margin?.mx ?? 0));
    const width = widgetCellWidth(parseWidgetWidth(widgetConfig), row.gap, marginX);
    // A widget filling a full-bleed page runs edge to edge, so only its own padding applies there.
    const padding =
        context.fillHeight && FILL_HEIGHT_WIDGET_TYPES.has(widget.type)
            ? {}
            : parseWidgetPadding(widgetConfig, cellPadding);
    const sticky = widgetConfig?.sticky === true;
    const bleeds = !!bleedWidth && widget.type === 'action-bar' && widgetConfig?.position === 'top';
    const bleedProps = bleeds
        ? { width: bleedWidth, mx: -PAGE_CONTENT_PADDING.px, mt: -PAGE_CONTENT_PADDING.py }
        : {};

    // Widgets are lazy-loaded, so the cell holds a framed placeholder while the chunk downloads
    // instead of collapsing to nothing. The widget types with no framed loading state of their
    // own keep an empty fallback, so nothing appears only to disappear once they mount.
    const skeletonVariant = WIDGET_TYPE_SKELETON_VARIANT[widget.type];
    // A list widget configured as cards loads as its cards, not as the table it shows by default.
    const cardConfig =
        skeletonVariant === 'table'
            ? parseCardViewConfig(widgetConfig, CARD_VIEW_DEFAULTS_BY_WIDGET[widget.type])
            : null;

    let fallback: ReactNode = null;
    if (widget.type === 'node-info') {
        // node-info builds its placeholder from its config, as its own loading state does.
        fallback = (
            <NodeInfoLoadingSkeleton
                config={widgetConfig}
                nodeId={context.nodeId}
                slug={context.slug}
            />
        );
    } else if (skeletonVariant) {
        fallback = (
            <WidgetFrame config={widgetConfig}>
                {cardConfig ? (
                    <WidgetSkeleton
                        {...cardSkeletonProps(cardConfig, {
                            favorite: widget.type === 'favorite-nodes',
                        })}
                    />
                ) : (
                    <WidgetSkeleton
                        variant={skeletonVariant}
                        minHeight={skeletonVariant === 'block' ? 400 : undefined}
                    />
                )}
            </WidgetFrame>
        );
    }

    return (
        /* Grid cell: it only positions and spaces the widget, and it sits outside the Suspense
           boundary so the fallback is laid out at the same width and padding as the widget.
           `bgColor`/`textColor` are applied by the widget's own WidgetFrame, to the container
           below the title. */
        <Box
            key={widgetIndex}
            width={width}
            height={context.fillHeight ? '100%' : undefined}
            {...margin}
            {...padding}
            {...bleedProps}
            // A pinned widget stays at the top of the scroll container while the rest of
            // the layout scrolls under it, so it has to outrank the sticky headers of the
            // data tables rendered below it (MUI stickyHeader cells sit at z-index 2).
            {...(sticky ? { position: 'sticky', top: 0, zIndex: stickyZIndex } : {})}
        >
            <Suspense fallback={fallback}>
                <WidgetComponent {...context} config={widgetConfig} />
            </Suspense>
        </Box>
    );
}

export function LayoutColumn({ column, context, gap, sidePanel, pageColumn }: LayoutColumnProps) {
    const theme = useTheme();
    const stickyZIndex = theme.zIndex.appBar;
    const { hasUserRole } = useAuth();
    // Widgets restricted by `roles` are dropped before layout, so they leave no empty cell or spacer.
    // With `keepWidgetSlots` the next widgets move up into the hidden ones' slots instead.
    const shownWidgets = visibleWidgets(
        column.widgets,
        hasUserRole,
        column.keepWidgetSlots ?? false,
    );
    const topWidgets = shownWidgets.filter(w => w.config?.position === 'top');
    const middleWidgets = shownWidgets.filter(
        w => !w.config?.position || (w.config.position !== 'top' && w.config.position !== 'bottom'),
    );
    const bottomWidgets = shownWidgets.filter(w => w.config?.position === 'bottom');
    const orderedWidgets = [...topWidgets, ...middleWidgets];
    const hasBottom = bottomWidgets.length > 0;
    // The single place tabs are filtered by role: tabs the user cannot see leave no empty box
    // behind, do not keep the column from filling a full-bleed page, and never reach ColumnTabs.
    const shownTabs = visibleTabs(column.tabs ?? [], hasUserRole);
    const hasTabs = shownTabs.length > 0;

    // On a full-bleed page, a column holding one widget that lays the page out itself (the
    // catalog) fills the page height edge to edge. Any other column scrolls as usual and gets
    // the breathing room the page no longer gives it.
    const fillsPage =
        !!context.fillHeight &&
        !hasTabs &&
        shownWidgets.length === 1 &&
        FILL_HEIGHT_WIDGET_TYPES.has(shownWidgets[0].type);
    // The main column of a list view holding a single data table gives it its whole height.
    const fillsColumn =
        !!pageColumn &&
        !context.nodeId &&
        !hasTabs &&
        !hasBottom &&
        shownWidgets.length === 1 &&
        isColumnFillingTable(shownWidgets[0]);
    const columnContext = { ...context, fillHeight: fillsPage || fillsColumn };
    // The main column of a full-bleed page (unless it is filled by the catalog) and of a detail
    // view gets the content padding every custom page has, and a side panel pads its content
    // itself: in those, that padding replaces the widget cells' default one, so the two do not add
    // up. A plain list view gets the content padding from CustomPageListView and keeps the cells'
    // default padding inside it. A widget's own `padding` still applies everywhere.
    const padsPageContent =
        !!pageColumn && !fillsPage && (!!context.fillHeight || !!context.nodeId);
    const columnPadding = padsPageContent ? PAGE_CONTENT_PADDING : {};
    const cellPadding = padsPageContent || sidePanel ? {} : DEFAULT_CELL_PADDING;
    // Without the cells' default padding nothing spaces two neighbouring widgets (e.g. dashboard
    // widgets with `width` < 1 side by side), so the column puts a gap between them: the 16px the
    // two cell paddings used to add up to.
    const columnGap = gap ?? (cellPadding === DEFAULT_CELL_PADDING ? undefined : PADDED_COLUMN_GAP);
    // The widths of the row's widgets give up their share of the gap actually laid out.
    const row: CellRow = {
        gap: theme.spacing(columnGap ?? 0),
        spacing: units => theme.spacing(units),
    };
    const bleedWidth = padsPageContent
        ? `calc(100% + ${theme.spacing(PAGE_CONTENT_PADDING.px * 2)})`
        : undefined;

    return (
        <Box
            display="flex"
            flexDirection="column"
            gap={columnGap}
            height={hasBottom || fillsPage || fillsColumn ? '100%' : undefined}
            {...columnPadding}
        >
            {/* The tabs sit in the same wrapping box as the widgets, as a full-width item, so a
                sticky widget's containing block spans the tab content and it stays pinned over it. */}
            <Box
                display="flex"
                flexWrap="wrap"
                gap={columnGap}
                height={fillsPage || fillsColumn ? '100%' : undefined}
            >
                {orderedWidgets.map((w, i) =>
                    renderWidget(w, columnContext, i, stickyZIndex, row, cellPadding, bleedWidth),
                )}
                {hasTabs && (
                    // Side and bottom margins keep the tab paper off the column edges, unless the
                    // column is already padded as page content.
                    <Box width="100%" px={padsPageContent ? 0 : 2} pb={padsPageContent ? 0 : 2}>
                        <ColumnTabs
                            tabs={shownTabs}
                            renderTab={tab => (
                                <LayoutColumn
                                    column={{ widgets: tab.widgets }}
                                    context={columnContext}
                                    gap={gap}
                                />
                            )}
                        />
                    </Box>
                )}
            </Box>
            {hasBottom && <Box flex={1} />}
            {hasBottom && (
                <Box display="flex" flexWrap="wrap" gap={columnGap}>
                    {bottomWidgets.map((w, i) =>
                        renderWidget(
                            w,
                            columnContext,
                            i,
                            stickyZIndex,
                            row,
                            cellPadding,
                            bleedWidth,
                        ),
                    )}
                </Box>
            )}
        </Box>
    );
}
